;(() => {
  const namespace = (globalThis.StylistLocator = globalThis.StylistLocator || {})

  // ============================================================================
  // Dependencies
  // ============================================================================

  const { constants = {}, templates = {}, geometry = {}, cluster = {}, customCluster = {} } = namespace
  const { MAP_STYLE = [] } = constants
  const { buildMarkerIcon = () => null, buildUserMarkerIcon = () => null } = templates
  const { calculateZoomForRadius = () => 12, determineRoleCategory = () => 'default' } = geometry
  const { CustomClusterRenderer } = cluster
  const { calculateDistance, CLUSTER_DISTANCE_METERS = 60 } = customCluster

  // ============================================================================
  // Constants
  // ============================================================================

  const DEFAULT_ZOOM = 12
  const DEFAULT_ZOOM_OUT = 4
  const DEFAULT_CENTER = { lat: 37.1103719, lng: -113.5925891 }
  const CLUSTERING_ZOOM_THRESHOLD = 24
  const MAX_SEARCH_RADIUS_MILES = 500
  const MILES_TO_METERS = 1609.34

  // Priority mapping for z-index ordering (higher numbers = higher priority)
  // Order: salon (highest) > elite > masters > certified_stylist (lowest)
  const CATEGORY_PRIORITY = {
    certified_salon: 40,
    elite: 30,
    masters: 20,
    certified_stylist: 10,
    default: 5,
  }

  // Offset added to cluster z-index to ensure they appear above regular pins of the same category
  const CLUSTER_ZINDEX_OFFSET = 5

  // ============================================================================
  // Distance-Based Clustering Algorithm
  // ============================================================================

  /**
   * Custom clustering algorithm for MarkerClusterer.
   * Clusters markers within a fixed physical distance (zoom-independent).
   */
  class DistanceBasedAlgorithm {
    constructor(distanceMeters = 60) {
      this.distanceMeters = distanceMeters
    }

    calculate({ markers }) {
      const clusters = []
      const processed = new Set()

      for (let i = 0; i < markers.length; i++) {
        if (processed.has(i)) continue

        const marker = markers[i]
        const position = marker.getPosition()
        if (!position) continue

        const clusterMarkers = [marker]
        processed.add(i)

        for (let j = i + 1; j < markers.length; j++) {
          if (processed.has(j)) continue

          const otherMarker = markers[j]
          const otherPosition = otherMarker.getPosition()
          if (!otherPosition) continue

          const distance = calculateDistance(
            { lat: position.lat(), lng: position.lng() },
            { lat: otherPosition.lat(), lng: otherPosition.lng() }
          )

          if (distance <= this.distanceMeters) {
            clusterMarkers.push(otherMarker)
            processed.add(j)
          }
        }

        let avgLat = 0
        let avgLng = 0
        for (const m of clusterMarkers) {
          const pos = m.getPosition()
          avgLat += pos.lat()
          avgLng += pos.lng()
        }
        avgLat /= clusterMarkers.length
        avgLng /= clusterMarkers.length

        clusters.push({
          markers: clusterMarkers,
          position: { lat: avgLat, lng: avgLng },
        })
      }

      return { clusters }
    }
  }

  // ============================================================================
  // Map Creation
  // ============================================================================

  function createMap(mapContainer, center = DEFAULT_CENTER, zoom = DEFAULT_ZOOM_OUT) {
    if (!globalThis.google || !globalThis.google.maps) {
      throw new Error('Google Maps API not loaded')
    }

    // Calculate minimum zoom based on max search radius (250 miles)
    const minZoom = calculateZoomForRadius(MAX_SEARCH_RADIUS_MILES, null, mapContainer, center)

    // Greedy zoom on desktop, cooperative on mobile — otherwise a one-finger
    // drag inside the map traps the page and the user can't scroll past it.
    const isMobile = globalThis.matchMedia && globalThis.matchMedia('(max-width: 768px)').matches
    const gestureHandling = isMobile ? 'cooperative' : 'greedy'

    return new globalThis.google.maps.Map(mapContainer, {
      center,
      zoom,
      minZoom,
      disableDefaultUI: true,
      styles: MAP_STYLE,
      gestureHandling,
    })
  }

  // ============================================================================
  // Marker Management
  // ============================================================================

  /**
   * Get z-index for a regular marker based on category and active state.
   * @param {string} category - Marker category
   * @param {boolean} isActive - Whether the marker is currently selected
   * @returns {number} Z-index value
   */
  function getMarkerZIndex(category, isActive) {
    const basePriority = CATEGORY_PRIORITY[category] || CATEGORY_PRIORITY.default
    return basePriority + (isActive ? 1 : 0)
  }

  /**
   * Get z-index for a cluster marker based on highest priority category.
   * Clusters appear above regular pins of the same category.
   * @param {string} category - Highest priority category in the cluster
   * @returns {number} Z-index value
   */
  function getClusterZIndex(category) {
    const basePriority = CATEGORY_PRIORITY[category] || CATEGORY_PRIORITY.default
    return basePriority + CLUSTER_ZINDEX_OFFSET
  }

  /**
   * Determine the category for a stylist.
   * @param {Object} stylist - Stylist object
   * @returns {string} Category string
   */
  function getStylistCategory(stylist) {
    // Salon owners get star pin
    if (stylist.isSalonOwner === true) return 'certified_salon'

    return determineRoleCategory(stylist)
  }

  /**
   * Remove markers for stylists no longer in the list.
   * @param {Map} markers - Map of email to marker
   * @param {Array} stylists - Current stylists array
   */
  function cleanupRemovedMarkers(markers, stylists) {
    const currentEmails = new Set(stylists.map((s) => s.email))
    for (const [email, marker] of markers.entries()) {
      if (!currentEmails.has(email)) {
        marker.setMap(null)
        markers.delete(email)
      }
    }
  }

  /**
   * Update or create a marker for a stylist.
   * @param {Object} params - Parameters
   * @returns {google.maps.Marker} The marker
   */
  function updateOrCreateMarker({ google, markers, stylist, selectedStylistEmail, onMarkerClick, mapInstance }) {
    const isActive = stylist.email === selectedStylistEmail
    const category = getStylistCategory(stylist)

    let marker = markers.get(stylist.email)
    if (!marker) {
      marker = new google.maps.Marker({
        position: { lat: stylist.latitude, lng: stylist.longitude },
        title: stylist.name || '',
        ...(mapInstance && { map: mapInstance }),
      })
      if (onMarkerClick) {
        marker.addListener('click', () => onMarkerClick(stylist, marker))
      }
      markers.set(stylist.email, marker)
    }

    // Preserve certified_salon category once assigned
    marker.category = marker.category === 'certified_salon' || category === 'certified_salon'
      ? 'certified_salon'
      : category

    marker.setPosition({ lat: stylist.latitude, lng: stylist.longitude })

    const markerIcon = buildMarkerIcon(category, { active: isActive })
    if (markerIcon) marker.setIcon(markerIcon)

    marker.setZIndex(getMarkerZIndex(category, isActive))

    return marker
  }

  function buildMarkersForStylists({
    google,
    markers,
    stylists,
    selectedStylistEmail,
    onMarkerClick,
    mapInstance,
    attachToMap,
  }) {
    const result = []
    for (const stylist of stylists) {
      const marker = updateOrCreateMarker({
        google,
        markers,
        stylist,
        selectedStylistEmail,
        onMarkerClick,
        mapInstance: attachToMap ? mapInstance : null,
      })
      marker.setMap(attachToMap ? mapInstance : null)
      result.push(marker)
    }
    return result
  }

  function isValidStylistLocation(stylist) {
    return stylist?.email && Number.isFinite(stylist.latitude) && Number.isFinite(stylist.longitude)
  }

  function getValidStylists(stylists) {
    return stylists.filter((stylist) => isValidStylistLocation(stylist))
  }

  function updateMarkers(map, markers, stylists, selectedStylistEmail, onMarkerClick, markerClusterer = null) {
    if (!map || !globalThis.google) return

    const google = globalThis.google
    const currentZoom = map.getZoom() || DEFAULT_ZOOM
    const shouldCluster = currentZoom < CLUSTERING_ZOOM_THRESHOLD
    const validStylists = getValidStylists(stylists)

    // Clean up markers for stylists no longer in the list
    cleanupRemovedMarkers(markers, stylists)

    if (shouldCluster && markerClusterer && globalThis.markerClusterer) {
      markerClusterer.clearMarkers()
      const clusterMarkers = buildMarkersForStylists({
        google,
        markers,
        stylists: validStylists,
        selectedStylistEmail,
        onMarkerClick,
        mapInstance: map,
        attachToMap: false,
      })

      markerClusterer.addMarkers(clusterMarkers)
      return
    }

    // Non-clustered mode
    if (markerClusterer) {
      markerClusterer.clearMarkers()
    }

    buildMarkersForStylists({
      google,
      markers,
      stylists: validStylists,
      selectedStylistEmail,
      onMarkerClick,
      mapInstance: map,
      attachToMap: true,
    })
  }

  // ============================================================================
  // Clusterer Initialization
  // ============================================================================

  function initializeClusterer(map, onClusterClick) {
    if (!map || !globalThis.google || !globalThis.markerClusterer) return null

    const MarkerClusterer = globalThis.markerClusterer.MarkerClusterer
    if (!MarkerClusterer) return null

    const renderer = CustomClusterRenderer ? new CustomClusterRenderer(onClusterClick) : undefined
    const algorithm = new DistanceBasedAlgorithm(CLUSTER_DISTANCE_METERS)

    return new MarkerClusterer({
      map,
      markers: [],
      renderer,
      algorithm,
      onClusterClick: onClusterClick || undefined,
    })
  }

  // ============================================================================
  // Radius Circle
  // ============================================================================

  function updateRadiusCircle(map, radiusCircle, radiusMiles, center) {
    if (!map || !globalThis.google || !globalThis.google.maps) {
      return radiusCircle
    }

    const radiusMeters = radiusMiles * MILES_TO_METERS

    if (!center || !radiusMiles || Number.isNaN(radiusMiles)) {
      if (radiusCircle) radiusCircle.setMap(null)
      return null
    }

    if (!radiusCircle) {
      radiusCircle = new globalThis.google.maps.Circle({
        strokeColor: '#c6ac78',
        strokeOpacity: 0.85,
        strokeWeight: 1.5,
        fillColor: '#c6ac78',
        fillOpacity: 0.12,
        map,
        center,
        radius: radiusMeters,
        clickable: false,
      })
    } else {
      radiusCircle.setMap(map)
    }

    radiusCircle.setCenter(center)
    radiusCircle.setRadius(radiusMeters)
    return radiusCircle
  }

  // ============================================================================
  // User Marker
  // ============================================================================

  function createUserMarker(map, coords) {
    if (!map || !globalThis.google) return null

    return new globalThis.google.maps.Marker({
      position: coords,
      map,
      title: 'Your location',
      icon: buildUserMarkerIcon(),
      zIndex: 3,
    })
  }

  // ============================================================================
  // Map Navigation
  // ============================================================================

  function centerMapOnLocation(map, coords, zoom) {
    if (!map) return
    map.setCenter(coords)
    map.setZoom(zoom)
  }

  function initializeUserLocation(onSuccess, onError) {
    if (!navigator.geolocation) {
      if (onError) onError()
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        }
        if (onSuccess) onSuccess(coords)
      },
      () => {
        if (onError) onError()
      }
    )
  }

  // ============================================================================
  // Exports
  // ============================================================================

  namespace.map = Object.assign({}, namespace.map, {
    DEFAULT_ZOOM,
    DEFAULT_ZOOM_OUT,
    DEFAULT_CENTER,
    CLUSTERING_ZOOM_THRESHOLD,
    MAX_SEARCH_RADIUS_MILES,
    CATEGORY_PRIORITY,
    getClusterZIndex,
    createMap,
    updateMarkers,
    updateRadiusCircle,
    createUserMarker,
    centerMapOnLocation,
    initializeUserLocation,
    initializeClusterer,
  })
})()
